import classes from './Appointments.module.css';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/fr';
import React from 'react';
import { getTimeSlots } from '../../../utils/scheduleHelpers';
import { ScheduleEntry } from './ScheduleEntry';
import calendarDaily from '../../../assets/icons/calendar-daily.svg';
import { useAppSelector } from '../../../store/store';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../../store/slices/auth';
import { DOCTOR_USER_ROLE } from '../../../constants/userRolesConstants';
import { useTranslation } from '../../../hooks/useTranslations';

interface Props {
  salesOrders: SalesOrderInfo[];
  selectedDate: Date;
  toggleWeeklyView: () => void;
  handleUpdateAppointment?: (
    salesOrder: SalesOrderInfo,
    start: Date,
    end: Date,
    callback: () => void
  ) => void;
  loadingUpdate?: boolean;
}

type DataToDisplay = {
  time: string;
  value: SalesOrderInfo[];
  firstAppointment: boolean;
};
const Appointments: React.FC<Props> = ({
  salesOrders,
  selectedDate,
  toggleWeeklyView,
  handleUpdateAppointment,
  loadingUpdate,
}) => {
  const lang = localStorage.getItem('lang') || 'en';
  const dateWithMoment = React.useMemo(() => {
    moment.locale(lang);
    return moment(selectedDate);
  }, [selectedDate,lang]);

  const { translations } = useTranslation();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const userRole: string = useAppSelector<string>(selectUserRole);

  const mappedData = React.useMemo(() => {
    const dataToDisplay: DataToDisplay[] = [];
    const timeSlots: Date[] = getTimeSlots(selectedDate, 0, 24, 0.25);
    let firstAppointmentIndex = -1;
    timeSlots.forEach((tS, index) => {
      const _tS = moment(tS);
      let matchingSalesOrders: SalesOrderInfo[] = [];
      salesOrders.forEach((salesOrder) => {
        const nextTs = timeSlots[index + 1];
        if (salesOrder.sales_order_appointment?.start) {
          const startDate = moment(salesOrder.sales_order_appointment?.start);
          const _nextTs = moment(nextTs);
          if (startDate.isBetween(_tS, _nextTs, undefined, '[)')) {
            matchingSalesOrders.push(salesOrder);
          }
        }
      });
      if (firstAppointmentIndex === -1 && matchingSalesOrders.length > 0) {
        firstAppointmentIndex = index;
      }
      dataToDisplay.push({
        time: `${_tS.format('HH')}:${_tS.format('mm')}`,
        value: matchingSalesOrders,
        firstAppointment: firstAppointmentIndex === index,
      });
    });
    return dataToDisplay;
  }, [selectedDate, salesOrders]);

  const groupByHour = React.useMemo(() => {
    const grouped: { slots: DataToDisplay[] }[] = [];
    for (let i = 0; i < mappedData.length; i += 4) {
      grouped.push({
        slots: mappedData.slice(i, i + 4),
      });
    }
    return grouped;
  }, [mappedData]);

  React.useEffect(() => {
    const el = document.getElementById(`firstAppointment`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedDate]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.textContainer}>
          <div className={classes.headerTitle}>
            {selectedDate.getDate() === new Date().getDate()
              ? translations('todays')
              : dateWithMoment.format('dddd')}{' '}
            {translations('appointments')}
          </div>
          <div className={classes.headerSubTitle}>
            <b>{dateWithMoment.format('dddd')}</b> {dateWithMoment.format('DD.MM.yyyy')}
          </div>
        </div>
        <div className={classes.headerRightContainer}>
          {accountInfo.serves_consultations && userRole !== DOCTOR_USER_ROLE && (
            <div className={classes.colorInfoContainer}>
              <div className={classes.colorIcon} style={{ backgroundColor: '#DBDFEA' }} />
              <div className={classes.colorText}>{translations('label.unassigned_doctor')}</div>
            </div>
          )}
          {accountInfo.serves_diagnostics && (
            <div className={classes.colorInfoContainer}>
              <div className={classes.colorIcon} style={{ backgroundColor: '#FFFDE1' }} />
              <div className={classes.colorText}>{translations('schedule.diagnostics')}</div>
            </div>
          )}
          {accountInfo.serves_consultations && (
            <div className={classes.colorInfoContainer}>
              <div className={classes.colorIcon} style={{ backgroundColor: '#E5F2FC' }} />
              <div className={classes.colorText}>{translations('schedule.consultations')}</div>
            </div>
          )}
          <img
            className={classes.toggleButton}
            src={calendarDaily}
            alt={'calendar'}
            onClick={toggleWeeklyView}
          />
        </div>
      </div>
      <div style={{ overflowY: 'auto', height: '100%' }}>
        {groupByHour.map((hour, index) => {
          return (
            <div
              key={index}
              className={classes.hour}
              id={
                hour.slots.find((slot) => slot.firstAppointment) !== undefined
                  ? 'firstAppointment'
                  : `row${index}`
              }
            >
              {hour.slots.map((data) => (
                <ScheduleEntry
                  key={`${data.time}}`}
                  salesOrder={data.value[0]}
                  salesOrders={data.value}
                  timeSlot={data.time}
                  handleUpdateAppointment={(salesOrder, start, end, callback) => {
                    handleUpdateAppointment &&
                      handleUpdateAppointment(salesOrder, start, end, callback);
                  }}
                  loadingUpdate={loadingUpdate}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { Appointments };
